import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, UrlTree, CanActivateFn } from '@angular/router';
import { switchMap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { DialogService } from 'primeng/dynamicdialog';

import { DashboardServiceService } from '@admin/shared/services/dashboard-service.service';

import { TrialExpiredComponent } from '@common/popups/trial-expired/trial-expired.component';
import { PlanExpiredPopupComponent } from '@common/popups/plan-expired-popup/plan-expired-popup.component';
import { PlanUpgradePopupComponent } from '@common/popups/plan-upgrade-popup/plan-upgrade-popup.component';

import { UserProfileService } from '@core/services/user.service';

// Define routes to be ignored
const ignoredRoutes = [
  '/',
  '/plans',
  '/checkout',
  '/account-setting',
  '/workspace/settings',
  '/auth/signup',
  '/auth/login',
  '/auth/forget-password',
  '/auth/reset-password',
  '/auth/verify-2fa',
]; // Add routes to be ignored here

export const billingGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state
): Observable<boolean | UrlTree> => {
  const dashboardService = inject(DashboardServiceService);
  const userProfileService = inject(UserProfileService);
  const dialogService = inject(DialogService);

  // Get the cached user if available
  return userProfileService.getUser().pipe(
    switchMap((cachedUser) => {
      if (cachedUser) {
        return processUser(cachedUser, state.url, dashboardService, dialogService);
      }
      // Fetch user details if not cached
      return userProfileService.getUserDetails().pipe(
        switchMap((user) => processUser(user, state.url, dashboardService, dialogService)),
        catchError((err) => {
          console.error('Error in billing guard:', err);
          return of(false); // Deny access if an error occurs
        })
      );
    }),
    catchError((err) => {
      console.error('Error in billing guard:', err);
      return of(false); // Deny access if an error occurs
    })
  );
};

// Function to process user and check trial status
function processUser(
  user: any,
  currentUrl: string,
  dashboardService: DashboardServiceService,
  dialogService: DialogService
): Observable<boolean> {
  const urlWithoutQueryParams = currentUrl.split('?')[0];
  // Get the workspace from the dashboard service
  return dashboardService.getWorkspace().pipe(
    switchMap((cachedWorkspace) => {
      if (cachedWorkspace) {
        if (ignoredRoutes.includes(urlWithoutQueryParams)) {
          return of(true); // Allow access without additional checks
        }
        // Workspace is cached, use it
        if (isTrialExpired(cachedWorkspace)) {
          openTrialExpiredDialog(dialogService);
          return of(false);
        }
        if (noActivePlan(cachedWorkspace)) {
          noActivePlanDialog(dialogService);
          return of(false);
        }
        if (isPlanExpired(cachedWorkspace)) {
          openPlanExpiredDialog(dialogService);
          return of(false);
        }
        return of(true);
      } else {
        // Workspace is not cached, find it in user details
        if (user && user.workspaces && user.workspaces.length) {
          const currentWorkspace = user.workspaces.find(
            (workspace: { uuid: any }) => workspace.uuid === user.settings.active_workspace
          );
          if (currentWorkspace) {
            // Set the workspace in the dashboard service
            dashboardService.setWorkspace(currentWorkspace);

            if (ignoredRoutes.includes(currentUrl)) {
              return of(true); // Allow access without additional checks
            }
            if (isTrialExpired(currentWorkspace)) {
              openTrialExpiredDialog(dialogService);
              return of(false);
            }
            if (noActivePlan(currentWorkspace)) {
              noActivePlanDialog(dialogService);
              return of(false);
            }

            if (isPlanExpired(currentWorkspace)) {
              openPlanExpiredDialog(dialogService);
              return of(false);
            }

            return of(true);
          } else {
            console.warn('No current workspace found');
            dashboardService.setWorkspace(user.workspaces[0]);
            return of(false); // No workspace found, deny access
          }
        } else {
          console.warn('No workspaces available');
          return of(false); // No workspaces available, deny access
        }
      }
    }),
    catchError((err) => {
      // Handle errors
      console.error('Error in billing guard:', err);
      return of(false); // Deny access if an error occurs
    })
  );
}

// Helper function to determine if the trial has expired
function isTrialExpired(workspaceDetails: any): boolean {
  if (workspaceDetails.planInfo.trialInfo.isInTrial) {
    return workspaceDetails.planInfo.trialInfo.has_expired_trial;
  } else {
    return false;
  }
}

// Helper function to open the trial expired dialog and return a result observable
function openTrialExpiredDialog(dialogService: DialogService): Observable<boolean> {
  dialogService.open(TrialExpiredComponent, {
    styleClass: 'trialModal',
    width: '900px',
    breakpoints: { '910px': '90vw' },
  });
  return of(true);
}

function noActivePlan(workspaceDetails: any): boolean {
  if (!workspaceDetails.planInfo.trialInfo.isInTrial) {
    if (!workspaceDetails.planInfo.subscriptionInfo.subscription) return true;
  }
  return false;
}

function isPlanExpired(workspaceDetails: any): boolean {
  if (!workspaceDetails.planInfo.trialInfo.isInTrial) {
    //  if (workspaceDetails.planInfo.subscriptionInfo?.subscription?.status !== 'active') return true;
    const endDate = workspaceDetails.planInfo.subscriptionInfo?.subscription?.renews_at;
    if (!endDate) return false;
    return new Date(endDate).toISOString() < new Date().toISOString();
  }
  return false;
}

function openPlanExpiredDialog(dialogService: DialogService): Observable<boolean> {
  dialogService.open(PlanExpiredPopupComponent, {
    styleClass: 'trialModal',
    width: '900px',
    breakpoints: { '910px': '90vw' },
  });
  return of(true);
}

function noActivePlanDialog(dialogService: DialogService): Observable<boolean> {
  dialogService.open(PlanUpgradePopupComponent, {
    closable: false,
    styleClass: 'trialModal',
    width: '900px',
    breakpoints: { '910px': '90vw' },
  });
  return of(true);
}
